// --- React and Redux
import React from 'react';
import {useSelector} from 'react-redux';
// --- Actions and Hooks
import {useCommentsActions} from '../../../../store/comments/useComments';
import {useClearanceMemoTitlesActions} from '../../../../store/clearanceMemoTitles/useClearanceMemoTitles';
import {useCentFileDialog} from '../../../../components/dialogs/dialogs/centralFileNum';
import {useCopyrightDialog} from '../../../../components/dialogs/dialogs/copyright';
import {useDeleteDialog} from '../../../../components/dialogs/dialogs/delete';
import {useAgGridApi} from '../../../../components/AGGrid/hooks';
import {useLoading} from '../../../../utils/hooks/useLoading';
// --- UI Components
import Button from '@dtci/spellbook/Button';
import {EditCommentModal} from '../../../../components/CommentModals/EditCommentModal';
import {AttachmentsModal} from '../../../../components/CommentModals/AttachmentsModal';
import {ViewCommentsModal} from '../../../../components/CommentModals/ViewCommentsModal';
import {CreateCommentModal} from '../../../../components/CommentModals/CreateCommentModal';
import {DetailsModal} from '../../../../components/DetailsModal';
import {MemoForm} from '../../../../components/ClearanceMemo/memo-form';
// --- Utilities
import {datastageApi} from '../../../../api';
import {filterCriteria} from '../../../../utils/filterCriteria';
import {EditSectionModal} from "../../../../components/ClearanceMemo/memo-form/edit-modal";
import {useClearanceMemoGridUtils} from "./utils/gridUtils";
import DataGrid from "@dtci/spellbook/DataGrid";
import styles from "../../../../components/AGGrid/details-table.module.scss";
import {usePermissions} from "../../../../utils/permissions";

/**
 * ClearanceMemoTitlesGrid Component
 *
 * Renders a grid for displaying and managing clearance memo titles.
 * Includes modals for viewing, editing, and deleting titles.
 *
 * @returns {JSX.Element} The rendered Clearance Memo Titles Grid component.
 */
export const ClearanceMemoTitlesGrid = () => {
    const agGridApi = useAgGridApi();
    const {
        titles,
        legalStatusOptions,
        titleRange,
        defaultFilterRange,
        titleSort,
        filterForm,
        isMemoModalOpen,
        requestor,
    } = useSelector((state) => state.titles);
    const {
       filterForm: commentFilterForm,
    } = useSelector((state) => state.comments);

    const {isLegalUser, isLegalAdmin} = usePermissions();
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {openCentFileDialog, openCopyrightDialog, openDeleteDialog} = {
        ...useCentFileDialog(),
        ...useCopyrightDialog(),
        ...useDeleteDialog(),
    };
    const {updateClearanceMemoTitles} = useClearanceMemoTitlesActions();
    const {updateComments, updateCommentsFilterForm} = useCommentsActions();

    const {
        setSort,
        columns
    } = useClearanceMemoGridUtils({
        datastageApi,
        filterForm,
        defaultFilterRange,
        updateClearanceMemoTitles,
        legalStatusOptions,
        isLegalUser,
        isLegalAdmin,
        handlers: {
            setLoadingTrue,
            setLoadingFalse,
            columnHandlers: [
                (data) => () => handleCommentsModalOpen(data),
                (data) => () => openCentFileDialog(data),
                (data) => () => openCopyrightDialog(data),
                (data) => () => handleDelete(data),
                (data) => () => handleModalOpen(data, false, true),
                (data) => () => handleModalOpen(data),
                (data) => () => handleModalOpen(data, true),
            ]

        }
    });


    // Fetch updated titles from the API
    const fetchUpdatedTitles = async () => {
        const cmTitles = await datastageApi.clearanceMemoTitles(
            filterCriteria(filterForm, '', 'clearanceMemoTitles'),
            titleRange,
            titleSort,
        );

        // Map legal status labels to the titles without mutating the state
        return cmTitles?.content?.map((item) => ({
            ...item,
            legalStatusId:
                legalStatusOptions.find((lookup) => lookup.value === item?.legalStatusId)?.label ||
                null,
        }));
    };

    // Handle opening the modal for viewing or editing a clearance memo
    const handleModalOpen = async (data, isViewOnly = false, isCreate = false) => {
        try {
            setLoadingTrue();
            let product = {};

            if (data?.mappedProductId) {
                if (isCreate) await datastageApi.createClearanceMemo(data?.mappedProductId);
                product = await datastageApi.productById(data?.mappedProductId);
            }
            const toc = await datastageApi.clearanceMemoTOC(data?.id);
            updateClearanceMemoTitles({
                isMemoModalOpen: true,
                isViewOnly,
                title: data,
                clearanceMemoTOC: toc,
                product,
            });
        } catch (error) {
            console.error('Error handling modal open:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Handle opening delete dialog
    const handleDelete = (data) => {
        // Open delete confirmation dialog
        openDeleteDialog(
            'Yes, Delete Clearance Memo',
            [
                `Are you sure you want to permanently delete the Clearance Memo and Clearance Report for ${data.title} (${data.id})?`,
                'This will also remove all product comments and attachments. This action cannot be reversed.',
            ],
            async () => {
                try {
                    setLoadingTrue();
                    await datastageApi.deleteClearanceMemo(data.id);
                    const updatedData = await fetchUpdatedTitles();
                    updateClearanceMemoTitles({
                        titles: updatedData,
                        totalPages: updatedData?.totalPages,
                        currentPage: updatedData?.pageable?.pageNumber,
                        totalItems: updatedData?.totalElements,
                    });
                } catch (error) {
                    console.error('Error deleting clearance memo:', error);
                } finally {
                    setLoadingFalse();
                }
            },
        );
    };

    const handleCommentsModalOpen = async (data) => {

        setLoadingTrue();

        const commentProduct = {
            productId: data?.mappedProductId,
            foxFinancialTitleId: data?.foxFinancialTitleId,
            clearanceMemoTitleId: data?.id,
        }

        // Fetch comments from API
        const commentList = await datastageApi.comments(
            filterCriteria(commentFilterForm, commentProduct, 'comments'),
            [0,19],
            ["commentType", "DESC"],
        );

        // Update comment filter form
        updateCommentsFilterForm({
            key: 'commentProduct',
            data: commentProduct,
        });

        // Update comment list and open comment modal with fetched comments
        updateComments({
            isCommentModalOpen: true,
            isClearancesComment: true,
            comments: commentList?.content || [],
            totalPages: commentList?.totalPages || 1,
            totalItems: commentList?.totalElements || 0,
            currentPage: commentList?.pageable?.pageNumber,
        });

        setLoadingFalse();
    };

    const handleCloseMemoModal = () => {
        updateClearanceMemoTitles({isMemoModalOpen: false});
    };


    return (
        <>
            <DataGrid
                className={styles.dataGrid}
                autoHeight
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: titles,
                    headerHeight: 45,
                }}
                bordered="none"
                columnDefs={columns}
                sortable={false}
                striped
                noResultsMessage={'No Clearance Memo Titles'}
            />
            <DetailsModal
                isOpen={isMemoModalOpen}
                onClose={handleCloseMemoModal}
                modalHeader="Clearance Memo"
                footer={
                    <Button sizeType="small" onClick={handleCloseMemoModal}>
                        Close
                    </Button>
                }
            >
                <MemoForm/>
            </DetailsModal>
            <EditSectionModal/>
            <ViewCommentsModal/>
            <CreateCommentModal/>
            <EditCommentModal/>
            <AttachmentsModal/>
        </>
    );
}
